import React from 'react';
import { Link } from 'react-router-dom';
import { Mail } from 'lucide-react';

/**
 * Reusable scholarly profile template used by all custom staff profile pages.
 *
 * Props:
 *   person: { id, name, honorific, title, affiliation, image, email, category, testid }
 *   children: profile section JSX (use <ProfileSection>, <ProfileBullets>, etc.)
 */
const StaffProfileTemplate = ({ person, children }) => {
  return (
    <div className="min-h-screen bg-white" data-testid={person.testid}>
      {/* Breadcrumb */}
      <div className="bg-stone-50 border-b border-stone-200">
        <div className="container mx-auto px-4 py-4">
          <nav className="flex items-center text-sm text-stone-600">
            <Link to="/" className="hover:text-green-600 transition-colors">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/people" className="hover:text-green-600 transition-colors">People</Link>
            <span className="mx-2">/</span>
            <span className="text-stone-900">{person.name}</span>
          </nav>
        </div>
      </div>

      {/* Identity Header */}
      <section className="bg-gradient-to-b from-stone-50 to-white py-16 border-b border-stone-200">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col md:flex-row gap-8 items-start">
              <div className="flex-shrink-0">
                <div className="w-48 h-48 rounded-lg overflow-hidden bg-stone-200 shadow-lg">
                  <img src={person.image} alt={person.name} className="w-full h-full object-cover" />
                </div>
              </div>
              <div className="flex-1">
                <div className="mb-3">
                  <span className="inline-block px-3 py-1 bg-green-100 text-green-800 text-xs font-medium uppercase tracking-wider rounded-full">
                    {person.category}
                  </span>
                </div>
                <h1 className="text-4xl font-serif font-bold text-stone-900 mb-2">
                  {person.honorific && <span className="text-stone-600">{person.honorific} </span>}
                  {person.name}
                </h1>
                <p className="text-xl text-stone-700 font-medium mb-4">{person.title}</p>
                {person.affiliation && (
                  <p className="text-stone-600 leading-relaxed">{person.affiliation}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">{children}</div>
      </div>

      {/* Footer Note */}
      <div className="bg-stone-50 border-t border-stone-200 py-8">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-sm text-stone-500 font-serif italic">
              This profile forms part of The Highland Institute's living scholarly archive, documenting research trajectories, collaborative commitments, and the intellectual work shaping the Institute's wider academic community.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export const ProfileSection = ({ title, children, last = false }) => (
  <>
    <section className="mb-16">
      <h2 className="text-2xl font-serif font-bold text-stone-900 mb-6 pb-2 border-b-2 border-stone-200">
        {title}
      </h2>
      <div className="prose prose-stone max-w-none">{children}</div>
    </section>
    {!last && (
      <div className="my-12 flex items-center justify-center">
        <div className="h-px w-24 bg-stone-300"></div>
      </div>
    )}
  </>
);

export const ProfileParagraphs = ({ items }) => (
  <>
    {items.map((p, i) => (
      <p key={i} className="text-stone-700 leading-relaxed mb-4 last:mb-0">{p}</p>
    ))}
  </>
);

export const ProfileBullets = ({ items, intro }) => (
  <>
    {intro && <p className="text-stone-700 leading-relaxed mb-4">{intro}</p>}
    <ul className="space-y-2">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-3 text-stone-700">
          <span className="text-green-600 mt-1">•</span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  </>
);

export const ProfileLabeledBullets = ({ items }) => (
  <ul className="space-y-4">
    {items.map((item, i) => (
      <li key={i} className="flex items-start gap-3 text-stone-700">
        <span className="text-green-600 mt-1">•</span>
        <div>
          <div className="font-semibold text-stone-900 mb-1">{item.label}</div>
          <div className="text-stone-700 leading-relaxed">{item.text}</div>
        </div>
      </li>
    ))}
  </ul>
);

export const ProfilePublications = ({ items }) => (
  <ul className="space-y-4">
    {items.map((pub, i) => (
      <li key={i} className="text-stone-700 leading-relaxed pl-4 border-l-2 border-green-600">
        {pub.year && <span className="font-medium text-stone-900">{pub.year} </span>}
        {pub.year && '— '}
        {pub.citation}
        {pub.url && (
          <>
            {' '}
            <a
              href={pub.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-green-700 hover:text-green-800 underline break-all"
            >
              {pub.url}
            </a>
          </>
        )}
      </li>
    ))}
  </ul>
);

export const ProfileLinkList = ({ items }) => (
  <ul className="space-y-2">
    {items.map((link, i) => (
      <li key={i} className="flex items-start gap-3 text-stone-700">
        <span className="text-green-600 mt-1">•</span>
        <a
          href={link.url}
          target="_blank"
          rel="noopener noreferrer"
          className="hover:text-green-600 transition-colors break-all"
          data-testid={link.testid}
        >
          {link.label}
        </a>
      </li>
    ))}
  </ul>
);

/**
 * Contact strip — common to all staff profiles
 * Props:
 *   email, role, scholarlyProfiles?: [{label, url, testid?}], hasInstitution = true
 */
export const ProfileContactSection = ({ email, role, scholarlyProfiles = [], affiliationLines, testidPrefix }) => (
  <ProfileSection title="Contact & Scholarly Infrastructure" last={true}>
    <div className="grid md:grid-cols-2 gap-8 not-prose">
      <div className="bg-stone-50 p-6 rounded-lg">
        <h3 className="font-semibold text-stone-900 mb-3">Institutional Affiliation</h3>
        <p className="text-stone-700 leading-relaxed text-sm">
          {(affiliationLines || ['The Highland Institute', 'Kohima, Nagaland']).map((line, i) => (
            <React.Fragment key={i}>
              {line}
              {i < (affiliationLines || ['', '']).length - 1 && <br />}
            </React.Fragment>
          ))}
        </p>
      </div>
      {email && (
        <div className="bg-stone-50 p-6 rounded-lg">
          <h3 className="font-semibold text-stone-900 mb-3">Email</h3>
          <div className="flex items-center gap-3 text-stone-700">
            <Mail className="w-5 h-5 text-green-600" />
            <a
              href={`mailto:${email}`}
              className="hover:text-green-600 transition-colors break-all"
              data-testid={`${testidPrefix}-email-link`}
            >
              {email}
            </a>
          </div>
        </div>
      )}
    </div>

    {role && (
      <div className="mt-8 not-prose">
        <h3 className="font-semibold text-stone-900 mb-4">Professional Role</h3>
        <ul className="space-y-2">
          <li className="flex items-start gap-3 text-stone-700">
            <span className="text-green-600 mt-1">•</span>
            <span>{role}</span>
          </li>
        </ul>
      </div>
    )}

    {scholarlyProfiles.length > 0 && (
      <div className="mt-8 not-prose">
        <h3 className="font-semibold text-stone-900 mb-4">Professional &amp; Scholarly Profiles</h3>
        <ProfileLinkList items={scholarlyProfiles} />
      </div>
    )}
  </ProfileSection>
);

export default StaffProfileTemplate;
